// Story 6.9 (ADO-1163, AC4) — Data Table (NEW primitive). Admin Users table
// (Figma 572:3553). Semantic <table> with <th scope="col">; horizontally
// scrollable on narrow viewports (the page body never scrolls sideways).

import type { ReactNode } from 'react';
import styles from './Table.module.css';

export interface TableColumn<Row> {
  key: string;
  header: ReactNode;
  /** Cell renderer; defaults to `String(row[key])` when omitted. */
  render?: (row: Row) => ReactNode;
}

export interface TableProps<Row> {
  caption: string;
  columns: TableColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** Shown when there are no rows. */
  emptyMessage?: ReactNode;
  /** Visually hide the caption (still available to assistive tech). */
  hideCaption?: boolean;
}

export function Table<Row>({
  caption,
  columns,
  rows,
  rowKey,
  emptyMessage = 'No records',
  hideCaption = true,
}: TableProps<Row>) {
  return (
    <div className={styles.scroll}>
      <table className={styles.table}>
        <caption
          style={
            hideCaption
              ? {
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  overflow: 'hidden',
                  clip: 'rect(0 0 0 0)',
                }
              : undefined
          }
        >
          {caption}
        </caption>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} scope="col" className={styles.th}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className={styles.empty} colSpan={columns.length}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={rowKey(row)} className={styles.row}>
                {columns.map((col) => (
                  <td key={col.key} className={styles.td}>
                    {col.render
                      ? col.render(row)
                      : String((row as Record<string, unknown>)[col.key] ?? '')}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
